import { ClaimPairingCodeRequestSchema, ClaimPairingCodeResponseSchema, CreatePairingSessionRequestSchema, PairingSessionCreatedSchema, PairingSessionPollSchema, DeviceCommandAckSchema, DeviceSyncRequestSchema, DeviceSyncResponseSchema, DeviceTimeResponseSchema, DeviceWallReadyRequestSchema, DeviceWallPlayingRequestSchema, IngestPlaybackEventsResponseSchema, DeviceWallReadyResponseSchema, PlaylistManifestSchema, } from '@ghostscreen/contracts';
import { DEVICE_COMMAND_ACK_PATH, DEVICE_MANIFEST_PATH, DEVICE_PAIRING_PATH, DEVICE_PAIRING_SESSIONS_PATH, DEVICE_SYNC_PATH, DEVICE_TIME_PATH, DEVICE_PLAYBACK_EVENTS_PATH, DEVICE_WALL_READY_PATH, DEVICE_WALL_PLAYING_PATH, } from '@ghostscreen/device-protocol';
export class DeviceApiError extends Error {
    status;
    body;
    constructor(status, body) {
        super(`Device API ${status}: ${body}`);
        this.status = status;
        this.body = body;
        this.name = 'DeviceApiError';
    }
}
export function createFetchDeviceApi(baseUrl, fetchImpl = fetch) {
    const root = baseUrl.replace(/\/$/, '');
    async function request(path, init) {
        const headers = new Headers(init.headers);
        if (init.token)
            headers.set('Authorization', `Bearer ${init.token}`);
        if (init.body && !headers.has('Content-Type')) {
            headers.set('Content-Type', 'application/json');
        }
        const { token: _token, ...rest } = init;
        const response = await fetchImpl(`${root}${path}`, { ...rest, headers });
        return response;
    }
    return {
        async claimPairing(body) {
            const payload = ClaimPairingCodeRequestSchema.parse(body);
            const response = await request(DEVICE_PAIRING_PATH, {
                method: 'POST',
                body: JSON.stringify(payload),
            });
            const text = await response.text();
            if (!response.ok)
                throw new DeviceApiError(response.status, text);
            return ClaimPairingCodeResponseSchema.parse(JSON.parse(text));
        },
        async createPairingSession(body) {
            const payload = CreatePairingSessionRequestSchema.parse(body);
            const response = await request(DEVICE_PAIRING_SESSIONS_PATH, {
                method: 'POST',
                body: JSON.stringify(payload),
            });
            const text = await response.text();
            if (!response.ok)
                throw new DeviceApiError(response.status, text);
            return PairingSessionCreatedSchema.parse(JSON.parse(text));
        },
        async pollPairingSession(code) {
            const response = await request(`${DEVICE_PAIRING_SESSIONS_PATH}/${encodeURIComponent(code.trim().toUpperCase())}`, { method: 'GET' });
            const text = await response.text();
            if (!response.ok)
                throw new DeviceApiError(response.status, text);
            return PairingSessionPollSchema.parse(JSON.parse(text));
        },
        async sync(token, body) {
            const payload = DeviceSyncRequestSchema.parse(body);
            const response = await request(DEVICE_SYNC_PATH, {
                method: 'POST',
                token,
                body: JSON.stringify(payload),
            });
            const text = await response.text();
            if (!response.ok)
                throw new DeviceApiError(response.status, text);
            return DeviceSyncResponseSchema.parse(JSON.parse(text));
        },
        async getManifest(token, input) {
            const qs = new URLSearchParams({
                playlistId: input.playlistId,
                version: String(input.version),
            });
            const response = await request(`${DEVICE_MANIFEST_PATH}?${qs}`, {
                method: 'GET',
                token,
            });
            const text = await response.text();
            if (!response.ok)
                throw new DeviceApiError(response.status, text);
            return PlaylistManifestSchema.parse(JSON.parse(text));
        },
        async ackCommand(token, body) {
            const payload = DeviceCommandAckSchema.parse(body);
            const response = await request(DEVICE_COMMAND_ACK_PATH, {
                method: 'POST',
                token,
                body: JSON.stringify(payload),
            });
            if (!response.ok) {
                throw new DeviceApiError(response.status, await response.text());
            }
        },
        async getTime(token) {
            const response = await request(DEVICE_TIME_PATH, { method: 'GET', token });
            const text = await response.text();
            if (!response.ok)
                throw new DeviceApiError(response.status, text);
            return DeviceTimeResponseSchema.parse(JSON.parse(text));
        },
        async reportWallReady(token, body) {
            const payload = DeviceWallReadyRequestSchema.parse(body);
            const response = await request(DEVICE_WALL_READY_PATH, {
                method: 'POST',
                token,
                body: JSON.stringify(payload),
            });
            const text = await response.text();
            if (!response.ok)
                throw new DeviceApiError(response.status, text);
            return DeviceWallReadyResponseSchema.parse(JSON.parse(text));
        },
        async reportWallPlaying(token, body) {
            const payload = DeviceWallPlayingRequestSchema.parse(body);
            const response = await request(DEVICE_WALL_PLAYING_PATH, {
                method: 'POST',
                token,
                body: JSON.stringify(payload),
            });
            if (!response.ok)
                throw new DeviceApiError(response.status, await response.text());
        },
        async reportPlaybackEvents(token, body) {
            // No local validation: a local error would be retried forever. The server validates
            // each event and returns rejected ids.
            const payload = body;
            const response = await request(DEVICE_PLAYBACK_EVENTS_PATH, {
                method: 'POST',
                token,
                body: JSON.stringify(payload),
            });
            const text = await response.text();
            if (!response.ok)
                throw new DeviceApiError(response.status, text);
            return IngestPlaybackEventsResponseSchema.parse(JSON.parse(text));
        },
        async downloadBytes(url) {
            const response = await fetchImpl(url, { cache: 'no-store', mode: 'cors' });
            if (!response.ok) {
                throw new DeviceApiError(response.status, `download failed for ${url}`);
            }
            return response.arrayBuffer();
        },
    };
}
//# sourceMappingURL=device-api.js.map